import { collection, doc, getDoc, getDocs, query, orderBy, limit } from "firebase/firestore";
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { db } from '../firebase-config';

/* =======================
   FETCH HELPERS
======================= */

const fetchTopRatedPerStandard = async () =>
{
    const snapshot = await getDocs(collection(db, "TopUsers"));
    const result = [];

    snapshot.forEach(doc =>
    {
        const data = doc.data();
        result.push({
            standard: Number(doc.id),
            handle: data.handle,
            rating: data.rating
        });
    });

    result.sort((a, b) => b.standard - a.standard);
    return result;
};

const fetchLastContestTop10 = async () =>
{
    const metaRef = doc(db, "Meta_Data", "Contests");
    const snapshot = await getDoc(metaRef);
    const latestContestID = snapshot.data().latestContest_ID;

    const standingsQuery = query(
        collection(db, "contests", latestContestID.toString(), "contest_results"),
        orderBy("totalScore", "desc"),
        limit(10)
    );

    const standingsSnap = await getDocs(standingsQuery);

    return standingsSnap.docs.map(doc =>
    {
        const data = doc.data();
        return {
            handle: data.userName,
            totalScore: data.totalScore
        };
    });
};

/* =======================
   GLOBAL LEADERBOARD
======================= */

const GlobalLeaderboard = () =>
{
    const [leaders, setLeaders] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() =>
    {
        const load = async () =>
        {
            const data = await fetchTopRatedPerStandard();
            setLeaders(data);
            setLoading(false);
        };
        load();
    }, []);

    if (loading)
    {
        return (
            <div className="mt-10 text-center text-xl sm:text-4xl">
                Loading Top Users...
            </div>
        );
    }

    return (
        <div className="border-2 border-black rounded-lg p-4 bg-white">
            <h2 className="text-xl sm:text-2xl font-bold mb-4 text-center">
                Top Rated by Standard
            </h2>

            <table className="w-full text-sm sm:text-lg">
                <thead className="border-b-2 border-gray-300">
                    <tr>
                        <th className="py-2 text-center">Std</th>
                        <th className="py-2 text-center">Handle</th>
                        <th className="py-2 text-center">Rating</th>
                        <th className="py-2 text-center">More</th>
                    </tr>
                </thead>

                <tbody>
                    {leaders.map(row => (
                        <tr
                            key={row.standard}
                            className="border-b hover:bg-gray-50 transition-colors"
                        >
                            <td className="py-2 text-center font-medium">
                                {row.standard}
                            </td>

                            <td className="py-2 text-center">
                                <Link
                                    to={`/profile/${row.handle}`}
                                    className="text-blue-600 hover:underline font-medium"
                                >
                                    {row.handle}
                                </Link>
                            </td>

                            <td className="py-2 text-center">
                                {row.rating}
                            </td>

                            <td className="py-2 text-center">
                                <Link
                                    to={`/leaderboard_rating/standard/${row.standard}/1`}
                                    className="text-blue-600 hover:text-blue-800 text-lg"
                                    title="View full leaderboard"
                                >
                                    →
                                </Link>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

/* =======================
   LAST CONTEST LEADERBOARD
======================= */

const rankBadge = (rank) =>
{
    if (rank === 1) return "🏆";
    if (rank === 2) return "🥈";
    if (rank === 3) return "🥉";

    const others = ["🔥", "⚡", "🎯", "⭐", "🚀", "💎", "🎖️"];
    return others[(rank - 4) % others.length];
};

const LastContestLeaderboard = () =>
{
    const [top10, setTop10] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() =>
    {
        const load = async () =>
        {
            const data = await fetchLastContestTop10();
            setTop10(data);
            setLoading(false);
        };
        load();
    }, []);

    if (loading)
    {
        return (
            <div className="mt-10 text-center text-xl sm:text-4xl">
                Loading Leaderboard...
            </div>
        );
    }

    return (
        <div className="border-2 border-black rounded-lg p-4 bg-white">
            <h2 className="text-xl sm:text-2xl font-bold mb-4 text-center">
                Last Contest — Top 10
            </h2>

            <table className="w-full text-sm sm:text-lg">
                <thead className="border-b-2 border-gray-300">
                    <tr>
                        <th className="py-2 text-center">#</th>
                        <th className="py-2 text-center">Handle</th>
                        <th className="py-2 text-center">Score</th>
                    </tr>
                </thead>

                <tbody>
                    {top10.map((row, idx) => (
                        <tr
                            key={idx}
                            className="border-b hover:bg-gray-50 transition-colors"
                        >
                            <td className="py-2 text-center font-medium">
                                {rankBadge(idx + 1)} {idx + 1}
                            </td>

                            <td className="py-2 text-center">
                                <Link
                                    to={`/profile/${row.handle}`}
                                    className="text-blue-600 hover:underline font-medium"
                                >
                                    {row.handle}
                                </Link>
                            </td>

                            <td className="py-2 text-center">
                                {row.totalScore}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

/* =======================
   PAGE
======================= */

const Ratings = () =>
{
    return (
        <div className="container mx-auto mt-6 px-2 sm:px-0">
            <div className="flex flex-col md:flex-row gap-6">
                <main className="w-full md:w-1/2">
                    <GlobalLeaderboard />
                </main>

                <aside className="w-full mb-1 md:w-1/2">
                    <LastContestLeaderboard />
                </aside>
            </div>
        </div>
    );
};

export default Ratings;